/**
 * WASM module initialization and Dioxus component mounting utilities.
 *
 * Usage (no props):
 *   const ref = useDioxus('mount_loading_full_page')
 *   return <div ref={ref} />
 *
 * Usage (with props):
 *   const ref = useDioxusWithProps('mount_copy_button', { content: 'hello', class: 'size-8' })
 *   return <div ref={ref} />
 *
 * ## Event bridging conventions
 *
 * Dioxus → React communication uses two patterns:
 *
 * 1. **`data-action` click delegation** — for simple button/toggle clicks.
 *    Dioxus renders `<button data-action="toggle-filter" data-type="sop">`.
 *    React listens with `el.addEventListener('click', ...)` and reads `data-*` attrs.
 *    Used by: TypeFilterBar, VersionItem, StepsEditing, AddStepButton, ConfirmDialog.
 *
 * 2. **CustomEvent with payload** — for events that carry data (e.g. seek time).
 *    Dioxus dispatches `new CustomEvent("clip-seek", { detail: timeValue })`.
 *    React listens with `el.addEventListener('clip-seek', ...)`.
 *    Used by: ClipTagBar (clip-seek), StepsPreview (step-seek).
 */

import { useRef, useEffect } from 'react'

type WasmModule = typeof import('../../wasm-pkg/linewise_wasm')

let wasmModule: WasmModule | null = null
let wasmInitPromise: Promise<WasmModule> | null = null

/** Initialize the WASM module (idempotent) */
export async function initWasm(): Promise<WasmModule> {
  if (wasmModule) return wasmModule
  if (wasmInitPromise !== null) return wasmInitPromise

  wasmInitPromise = (async () => {
    const mod = await import('../../wasm-pkg/linewise_wasm')
    await mod.default()
    wasmModule = mod
    return mod
  })()

  return wasmInitPromise
}

/** Mount functions that take only an element (no props) */
export type MountFn =
  | 'mount_loading_full_page'
  | 'mount_loading_table'
  | 'mount_loading_document'
  | 'mount_loading_document_page'
  | 'mount_loading_document_card_content'
  | 'mount_loading_documents_card'
  | 'mount_loading_home_page'
  | 'mount_loading_settings'
  | 'mount_loading_sidebar'
  | 'mount_loading_all_documents_content'
  | 'mount_loading_all_documents_page'
  | 'mount_loading_document_edit_header'
  | 'mount_loading_document_edit_content'
  | 'mount_add_step_button'

/** Mount functions that take an element + JSON props string */
export type MountFnWithProps =
  | 'mount_older_version_banner'
  | 'mount_copy_button'
  | 'mount_processing_stepper'
  | 'mount_post_process_banner'
  | 'mount_clip_tag_bar'
  | 'mount_steps_preview'
  | 'mount_error_document_edit'
  | 'mount_error_document_page'
  | 'mount_document_line_card'
  | 'mount_confirm_dialog'
  | 'mount_video_time_range_badge'
  | 'mount_sync_status_badge'
  | 'mount_sortable_header_cell'
  | 'mount_duration_cell'
  | 'mount_empty_table_state'
  | 'mount_workspace_admin_badge'
  | 'mount_type_filter_bar'
  | 'mount_draft_mode_banner'
  | 'mount_top_nav_settings'
  | 'mount_document_card_grid'
  | 'mount_preview_panel'
  | 'mount_version_item'
  | 'mount_mobile_settings_nav'

/**
 * React hook that mounts a Dioxus WASM component (no props) into a div ref.
 */
export function useDioxus(mountFn: MountFn) {
  const ref = useRef<HTMLDivElement>(null)
  const mounted = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || mounted.current) return

    initWasm().then((mod) => {
      if (!mounted.current && ref.current) {
        mounted.current = true
        mod[mountFn](ref.current)
      }
    })

    return () => {
      mounted.current = false
    }
  }, [mountFn])

  return ref
}

/**
 * React hook that mounts a Dioxus WASM component with JSON-serialized props.
 * Mounts once into a stable inner container. On prop changes, dispatches an
 * `update-props` CustomEvent so the Dioxus VDom updates its Signal reactively
 * without teardown/recreation.
 */
export function useDioxusWithProps<T extends Record<string, unknown>>(
  mountFn: MountFnWithProps,
  props: T,
) {
  const outerRef = useRef<HTMLDivElement>(null)
  const propsJson = JSON.stringify(props)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const mountedRef = useRef(false)
  const pendingPropsRef = useRef<string | null>(null)

  // First mount: create inner div and launch VDom
  useEffect(() => {
    const outer = outerRef.current
    if (!outer || mountedRef.current) return

    // StrictMode guard: if inner div is still in the DOM, VDom is alive — skip
    if (innerRef.current && outer.contains(innerRef.current)) {
      return
    }

    const inner = document.createElement('div')
    inner.className = 'contents'
    outer.appendChild(inner)
    innerRef.current = inner

    initWasm().then((mod) => {
      if (innerRef.current !== inner) return
      // Use the latest props (may have changed during async init)
      const jsonToMount = pendingPropsRef.current ?? propsJson
      pendingPropsRef.current = null
      ;(mod[mountFn] as (el: Element, json: string) => void)(inner, jsonToMount)
      mountedRef.current = true
    })

    return () => {
      if (innerRef.current && outer.contains(innerRef.current)) {
        innerRef.current.remove()
        innerRef.current = null
        mountedRef.current = false
        pendingPropsRef.current = null
      }
    }
  }, [mountFn]) // eslint-disable-line react-hooks/exhaustive-deps -- mount once

  // Prop updates: dispatch CustomEvent to the running VDom
  useEffect(() => {
    const inner = innerRef.current
    if (!inner) return

    if (!mountedRef.current) {
      // WASM still loading — queue for mount to pick up
      pendingPropsRef.current = propsJson
      return
    }

    inner.dispatchEvent(new CustomEvent('update-props', { detail: propsJson }))
  }, [propsJson])

  return outerRef
}

// ── useDioxusWithCallbacks ───────────────────────────────────────────
//
// Callback-bearing `mount_*` fns that bridge HTTP / toasts / cache
// invalidation take a heterogeneous list of trailing JS callbacks
// alongside the JSON props string (e.g.
// `mount_xxx(el, json, getToken, onSuccess, showToast)`). This hook
// keeps the one-shot mount + teardown lifecycle in one place.
//
// Usage:
//   const ref = useDioxusWithCallbacks({
//     mountFn: 'mount_reference_documents_table',
//     props: { tenantId, projectId, ... },
//     callbacks: [getFirebaseToken, onJobsPanelOpen, onSuccess, showToast],
//     // Re-mount whenever any of these change identity. By default we
//     // mount once; pass a key derived from open/closed state etc. to
//     // force a fresh mount.
//     remountKey: open ? 'open' : 'closed',
//   })
//   return <div ref={ref} />

/** Mount fns whose JS-side signature is
 * `(element, props_json, ...callbacks)`. Add to this union as new
 * callback-bearing mount fns land. */
export type MountFnWithCallbacks =
  | 'mount_maintenance_task_dialog'
  | 'mount_sop_generate_prompt_form'
  | 'mount_gen_ai_settings_form'
  | 'mount_document_jobs_panel'
  | 'mount_reference_documents_table'
  | 'mount_documents_table'
  | 'mount_diagnostics_tab'
  | 'mount_webauthn_enroll_dialog'
  | 'mount_webauthn_credential_list'
  | 'mount_mfa_reset_by_code_dialog'
  | 'mount_login_passkey_button'

// Use a permissive shape so wrappers can pass strongly-typed callbacks
// (e.g. `(documentJson: string) => void`) without bivariance noise.
// The hook never invokes the callbacks itself — they're handed to the
// WASM mount fn verbatim.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DioxusCallback = (...args: any[]) => any

type DioxusWithCallbacksOpts<T extends Record<string, unknown>> = {
  mountFn: MountFnWithCallbacks
  props: T
  callbacks: DioxusCallback[]
  /** When this string changes, the VDom is torn down and re-mounted.
   * Pass a stable string for "mount once". Pass an open/closed flag's
   * stringification when you want reset-on-open semantics. */
  remountKey?: string
}

/**
 * Mount a Dioxus WASM component that takes JSON props plus a tail of
 * JS callback functions. The callbacks are passed through to the WASM
 * binding verbatim — refs not signals, so callers wrapping closures
 * over React state should `useCallback` to avoid spurious re-mounts.
 *
 * The hook owns the inner container, the `initWasm` await, and the
 * teardown on `remountKey` change. Prop changes after mount are
 * forwarded via the `update-props` CustomEvent bridge, so the VDom
 * re-renders reactively without teardown.
 */
export function useDioxusWithCallbacks<T extends Record<string, unknown>>(
  opts: DioxusWithCallbacksOpts<T>,
) {
  const { mountFn, props, callbacks, remountKey = 'once' } = opts
  const outerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const mountedRef = useRef(false)
  const pendingPropsRef = useRef<string | null>(null)

  const propsJson = JSON.stringify(props)

  // Stash the latest callbacks in a ref so the mount effect picks up
  // fresh values without re-mounting on every render.
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  // Mount: keyed off `mountFn` + `remountKey`. `remountKey` lets
  // callers force a fresh mount (reset-on-open) without tracking the
  // teardown by hand.
  useEffect(() => {
    const outer = outerRef.current
    if (!outer) return

    if (innerRef.current && outer.contains(innerRef.current)) {
      return
    }

    const inner = window.document.createElement('div')
    inner.className = 'contents'
    outer.appendChild(inner)
    innerRef.current = inner

    initWasm().then((mod) => {
      if (innerRef.current !== inner) return
      const jsonToMount = pendingPropsRef.current ?? propsJson
      pendingPropsRef.current = null
      const fn = mod[mountFn] as (
        el: Element,
        json: string,
        ...rest: DioxusCallback[]
      ) => void
      fn(inner, jsonToMount, ...callbacksRef.current)
      mountedRef.current = true
    })

    return () => {
      if (innerRef.current && outer.contains(innerRef.current)) {
        innerRef.current.remove()
        innerRef.current = null
      }
      mountedRef.current = false
      pendingPropsRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount once per remountKey
  }, [mountFn, remountKey])

  // Forward prop changes after the initial mount via the
  // `update-props` CustomEvent bridge. While WASM is still loading,
  // queue the latest payload so the mount picks it up.
  useEffect(() => {
    const inner = innerRef.current
    if (!inner) return
    if (!mountedRef.current) {
      pendingPropsRef.current = propsJson
      return
    }
    inner.dispatchEvent(new CustomEvent('update-props', { detail: propsJson }))
  }, [propsJson])

  return outerRef
}
